/**
 * Arabise — game-y sound effects (Duolingo-flavoured).
 * Ported from legacy js/sounds.js. Pure Web Audio.
 *
 * Notes layer triangle + chorus + 5th + occasional octave for warmth.
 * Safari requires AudioContext creation from a user gesture; we defer
 * via pointerdown/touchstart/keydown listeners. Mute persists in
 * localStorage.
 */

const MUTE_KEY = "arabise-sound-muted";

let ctx: AudioContext | null = null;
let unlocked = false;

function makeCtxInGesture() {
  if (unlocked) return;
  unlocked = true;
  const Ctor = (window.AudioContext || (window as any).webkitAudioContext) as typeof AudioContext;
  if (!Ctor) return;
  try {
    ctx = new Ctor();
    if (ctx.state === "suspended") ctx.resume().catch(() => {});
  } catch { ctx = null; }
}

if (typeof window !== "undefined") {
  (["pointerdown", "touchstart", "keydown"] as const).forEach(evt => {
    window.addEventListener(evt, makeCtxInGesture, { capture: true, passive: true });
  });
  document.addEventListener("visibilitychange", () => {
    if (!document.hidden && ctx && ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }
  });
}

export function isMuted(): boolean {
  try { return localStorage.getItem(MUTE_KEY) === "1"; } catch { return false; }
}
export function setMuted(on: boolean): void {
  try { localStorage.setItem(MUTE_KEY, on ? "1" : "0"); } catch {}
}

type VoiceOpts = {
  freq?: number;
  type?: OscillatorType;
  dur?: number;
  gain?: number;
  attack?: number;
  decay?: number;
  delay?: number;
  detune?: number;
  slideTo?: number;
};

function voice(opts: VoiceOpts) {
  const c = ctx;
  if (!c || isMuted()) return;
  if (c.state === "suspended") {
    c.resume().catch(() => {});
    if (c.state === "suspended") return;
  }
  const now    = c.currentTime + (opts.delay || 0);
  const dur    = opts.dur     || 0.12;
  const freq   = opts.freq    || 440;
  const type   = opts.type    || "triangle";
  const peak   = Math.min(opts.gain || 0.10, 0.22);
  const attack = opts.attack  || 0.005;
  const decay  = opts.decay   || dur * 0.9;
  const detune = opts.detune  || 0;

  const osc = c.createOscillator();
  osc.type   = type;
  osc.detune.value = detune;
  osc.frequency.setValueAtTime(freq, now);
  if (opts.slideTo) {
    osc.frequency.exponentialRampToValueAtTime(Math.max(1, opts.slideTo), now + dur);
  }

  const g = c.createGain();
  g.gain.setValueAtTime(0.0001, now);
  g.gain.exponentialRampToValueAtTime(peak,   now + attack);
  g.gain.exponentialRampToValueAtTime(0.0001, now + attack + decay);

  osc.connect(g);
  g.connect(c.destination);
  osc.start(now);
  osc.stop(now + dur + 0.05);
}

function note(opts: { freq: number; dur?: number; gain?: number; delay?: number }) {
  const f = opts.freq;
  const d = opts.dur  || 0.14;
  const g = opts.gain || 0.10;
  const t = opts.delay || 0;
  voice({ freq: f,       type: "triangle", dur: d, gain: g,        delay: t,          attack: 0.006, decay: d * 0.95 });
  voice({ freq: f,       type: "triangle", dur: d, gain: g * 0.55, delay: t, detune: 10 });
  voice({ freq: f * 1.5, type: "sine",     dur: d, gain: g * 0.35, delay: t });
}

/** Correct — E5 → G5 → C6 ta-da arpeggio. */
export function playCorrect() {
  note({ freq: 659.25, dur: 0.10, gain: 0.11, delay: 0.00 });
  note({ freq: 783.99, dur: 0.10, gain: 0.11, delay: 0.07 });
  note({ freq: 1046.5, dur: 0.16, gain: 0.13, delay: 0.14 });
}

/** Wrong — gentle F4 → D4 "uh-oh". */
export function playWrong() {
  voice({ freq: 349.23, type: "square",   dur: 0.13, gain: 0.10, attack: 0.01,  decay: 0.10, delay: 0    });
  voice({ freq: 349.23, type: "triangle", dur: 0.13, gain: 0.06, attack: 0.005, decay: 0.10, delay: 0, detune: 12 });
  voice({ freq: 293.66, type: "square",   dur: 0.20, gain: 0.10, attack: 0.005, decay: 0.18, delay: 0.10 });
  voice({ freq: 293.66, type: "triangle", dur: 0.20, gain: 0.06, attack: 0.005, decay: 0.18, delay: 0.10, detune: 12 });
}

/** Reveal — short rising whoosh on each new question. */
export function playReveal() {
  voice({ freq: 420, slideTo: 880, type: "sine", dur: 0.14, gain: 0.07, attack: 0.005, decay: 0.12 });
}

/** End-of-round fanfare — C5 → E5 → G5 → C6 → sustained E6 + bass. */
export function playFanfare() {
  const seq = [
    { f: 523.25, t: 0.00, d: 0.11 },
    { f: 659.25, t: 0.10, d: 0.11 },
    { f: 783.99, t: 0.20, d: 0.13 },
    { f: 1046.5, t: 0.32, d: 0.16 },
    { f: 1318.5, t: 0.48, d: 0.32 }
  ];
  seq.forEach(n => note({ freq: n.f, dur: n.d, gain: 0.12, delay: n.t }));
  voice({ freq: 261.63, type: "triangle", dur: 0.34, gain: 0.10, delay: 0.48, attack: 0.01, decay: 0.30 });
}

export function playStreakBump() {
  note({ freq: 659.25, dur: 0.10, gain: 0.10, delay: 0.00 });
  note({ freq: 880.00, dur: 0.10, gain: 0.10, delay: 0.07 });
  note({ freq: 1046.5, dur: 0.14, gain: 0.12, delay: 0.14 });
}

export function playTick() {
  voice({ freq: 1200, type: "square", dur: 0.03, gain: 0.05, attack: 0.001, decay: 0.02 });
}

/** Bell-like single-star ping; freq controls the pitch (ascending star ladder). */
export function playStarPop(freq: number) {
  const f = freq || 880;
  voice({ freq: f,       type: "triangle", dur: 0.20, gain: 0.13, attack: 0.003, decay: 0.18 });
  voice({ freq: f * 2,   type: "sine",     dur: 0.20, gain: 0.07, attack: 0.003, decay: 0.18 });
  voice({ freq: f * 1.5, type: "sine",     dur: 0.14, gain: 0.05, attack: 0.003, decay: 0.12, delay: 0.02 });
}

/** Three-star perfect flourish. */
export function playPerfect() {
  const seq = [
    { f: 783.99, t: 0.00, d: 0.10 },
    { f: 987.77, t: 0.08, d: 0.10 },
    { f: 1174.7, t: 0.16, d: 0.10 },
    { f: 1568.0, t: 0.28, d: 0.22 }
  ];
  seq.forEach(n => note({ freq: n.f, dur: n.d, gain: 0.12, delay: n.t }));
}

/**
 * End-of-session build-up. Star pops ascend in pitch, then the
 * fanfare lands. ≥80% adds the perfect flourish.
 *   ≥ 80%  → 3-star build + fanfare + perfect
 *   ≥ 50%  → 2-star build + fanfare
 *    > 0%  → 1-star build + fanfare
 */
export function playSessionEnd(correct: number, total: number) {
  if (!total || total <= 0) { playFanfare(); return; }
  if (correct <= 0) return;

  const pct   = (correct / total) * 100;
  const stars = pct >= 80 ? 3 : pct >= 50 ? 2 : 1;
  const STAR_BEAT_MS = 550;
  const STAR_NOTES   = [659.25, 880.00, 1318.5];

  for (let i = 0; i < stars; i++) {
    window.setTimeout(() => playStarPop(STAR_NOTES[i]), i * STAR_BEAT_MS);
  }
  const finishAt = stars * STAR_BEAT_MS + 180;
  window.setTimeout(() => {
    playFanfare();
    if (pct >= 80) window.setTimeout(playPerfect, 320);
  }, finishAt);
}
